import type {
	IAppDidMountHandler,
	IAppWillMountHandler,
	IRenderer,
	IStructureStore,
	IPagesModelsRegistrar,
	IAppWillUnmountHandler,
	IMaterializedStore } from '@wix/thunderbolt-symbols'
import {
	EditorFeatureConfigSymbol,
	LOADING_PHASES,
	LifeCycle,
	MasterPageFeatureConfigSymbol,
	PageFeatureConfigSymbol,
	PlatformSymbol,
	Props,
	RendererSymbol,
	SiteFeatureConfigSymbol,
	Structure,
	isViewerFragmentSymbol,
	CarmiInstance,
	PagesModelsRegistrarSymbol,
	MaterializedStoreSymbol,
	AppMaterializerSymbol,
} from '@wix/thunderbolt-symbols'
import type { Identifier, IocContainer } from '@wix/thunderbolt-ioc'
import type { ClientRenderResponse, RendererProps } from 'feature-react-renderer'
import {
	ManifestSymbol,
	ViewerBaseSymbol,
	ViewerSourceSymbol,
	ViewerManagerApi,
	getBaseStructure,
} from 'thunderbolt-viewer-manager-types'
import type { ViewerAPIProviderInitializer } from 'thunderbolt-viewer-manager-types'
import { ViewerManagerProvider } from 'thunderbolt-viewer-manager'
import { createEnvLoader } from '@wix/thunderbolt-environment'
import type { Environment } from '@wix/thunderbolt-environment'
import type { ContainerModulesValues, DSCarmi } from 'thunderbolt-ds-carmi-root'
import { initCustomElementsDS } from './initCustomElementsDS'
import type { DsApiFactoryEnv, DsApis } from './getDsApis'
import type { IComponentsLoader } from '@wix/thunderbolt-components-loader'
import { ComponentsLoaderSymbol } from '@wix/thunderbolt-components-loader'
import { ModelUpdatesInvoker } from 'ds-feature-model-updates-invoker'
import { taskify, isExperimentOpen } from '@wix/thunderbolt-commons'
import type { Materializer } from '@wix/thunderbolt-types'
import type { ByRefsis } from '@wix/thunderbolt-byref'
import type {
	Catharsis,
	MegaStoreWithSubscriptions,
	PrivateMegaStore } from '@wix/thunderbolt-catharsis'
import {
	CatharsisMegaStoreSymbol
} from '@wix/thunderbolt-catharsis'

const modulesToInjectToCarmi = {
	modelUpdatesInvoker: ModelUpdatesInvoker,
	props: Props,
	platform: PlatformSymbol,
}

export const initThunderboltDS = async ({
	catharsis,
	dsCarmi,
	byRefsis,
	pagesModelsRegistrar,
	environment,
	container,
	wixCustomElements,
	isViewerFragment = false,
}: {
	catharsis: Catharsis
	dsCarmi: DSCarmi
	byRefsis: ByRefsis
	pagesModelsRegistrar: IPagesModelsRegistrar
	environment: Omit<Environment, 'browserWindow'> & { browserWindow: DsApiFactoryEnv['window'] }
	container: IocContainer
	wixCustomElements: any
	isViewerFragment: boolean
}): Promise<DsApis> => {
	const window = environment.browserWindow
	container.bind(ViewerSourceSymbol).toConstantValue(window.viewerSource)
	container.bind(PagesModelsRegistrarSymbol).toConstantValue(pagesModelsRegistrar)
	container.bind(ViewerBaseSymbol).toConstantValue(window.viewerBase)
	container.bind(ManifestSymbol).toConstantValue(window.manifest)
	container.bind(CarmiInstance).toConstantValue(dsCarmi)
	container.bind(isViewerFragmentSymbol).toConstantValue(isViewerFragment)
	container.bind(ViewerManagerApi).toProvider(ViewerManagerProvider)

	const {
		container: dsContainer,
		editorFeatures,
		editorFeaturesConfigs,
		masterPageConfigs,
		siteFeaturesConfigs,
		allPageFeaturesConfigs,
		viewerModel,
		$runInBatch,
	} = dsCarmi

	Object.entries(editorFeaturesConfigs).forEach(([featureName, featureConfig]) => {
		container.bind(EditorFeatureConfigSymbol).toConstantValue(featureConfig).whenTargetNamed(featureName)
	})
	Object.entries(siteFeaturesConfigs).forEach(([featureName, featureConfig]) => {
		container.bind(SiteFeatureConfigSymbol).toConstantValue(featureConfig).whenTargetNamed(featureName)
	})
	Object.entries(masterPageConfigs).forEach(([featureName, featureConfig]) => {
		container.bind(MasterPageFeatureConfigSymbol).toConstantValue(featureConfig).whenTargetNamed(featureName)
	})
	Object.entries(allPageFeaturesConfigs).forEach(([featureName, featureConfig]) => {
		container.bind(PageFeatureConfigSymbol).toConstantValue(featureConfig).whenTargetNamed(featureName)
	})

	container.load(createEnvLoader(environment))

	await environment.specificEnvFeaturesLoaders?.loadEditorFeatures(container, editorFeatures)

	// Inject all modules to the carmi instance
	$runInBatch(() => {
		Object.entries(modulesToInjectToCarmi).forEach(([key, symbol]) => {
			const injectedModule = container.get<ContainerModulesValues>(symbol)
			dsContainer.setContainerModule(key, injectedModule)
		})
	})

	const appWillMountHandlers = container.getAll<IAppWillMountHandler>(LifeCycle.AppWillMountHandler)
	await Promise.all(
		appWillMountHandlers.map((appWillMountHandler) => taskify(() => appWillMountHandler.appWillMount()))
	)

	const renderer = container.get<IRenderer<RendererProps, Promise<ClientRenderResponse>>>(RendererSymbol)
	await renderer.init()

	const componentsLoader = container.get<IComponentsLoader>(ComponentsLoaderSymbol)
	const structureStore = container.get<IStructureStore>(Structure)

	environment.logger.phaseStarted(LOADING_PHASES.LOAD_INITIAL_DS_COMPONENTS)
	await Promise.all([
		componentsLoader.loadComponents(structureStore.getEntireStore()),
		componentsLoader.loadComponent('PageMountUnmount'),
	])
	environment.logger.phaseEnded(LOADING_PHASES.LOAD_INITIAL_DS_COMPONENTS)

	environment.logger.phaseStarted(LOADING_PHASES.INIT_DS_VIEWER_API)
	const viewerAPIProvider = await container.get<ViewerAPIProviderInitializer>(ViewerManagerApi)()
	environment.logger.phaseEnded(LOADING_PHASES.INIT_DS_VIEWER_API)
	const getModule = <T>(symbol: Identifier) => container.get<T>(symbol)
	const getNamedModule = <T>({ identifier, name }: { identifier: Identifier; name: string }) =>
		container.getNamed<T>(identifier, name)

	return {
		clean: () => {
			if (isExperimentOpen('specs.thunderbolt.clearStoresBeforeNavigate', environment.experiments)) {
				const viewerAPI = viewerAPIProvider.getViewerAPI()
				viewerAPI.actions.runInBatch(() => {
					dsCarmi.structure.setEntireStructure(getBaseStructure())
					// @ts-ignore
					const blankData = _.mapValues(dsCarmi.$model.data, (namespaceData) => {
						const masterPage = namespaceData.masterPage
						return masterPage ? { masterPage } : {}
					})
					dsCarmi.data.setEntireData(blankData)
				})
			}
			container.get<IMaterializedStore>(MaterializedStoreSymbol).unmount()
			container.get<Materializer>(AppMaterializerSymbol).cleanAll()
			dsCarmi.clean()
			byRefsis.cleanAll()
			catharsis.cleanAll()
			container.get<PrivateMegaStore<MegaStoreWithSubscriptions>>(CatharsisMegaStoreSymbol).cleanAll()
		},
		getViewerAPI: viewerAPIProvider.getViewerAPI,
		render: renderer.render,
		appDidMount: () => {
			const appDidMountHandlers = container.getAll<IAppDidMountHandler>(LifeCycle.AppDidMountHandler)
			appDidMountHandlers.forEach((appDidMountHandler) => appDidMountHandler.appDidMount())
		},
		appWillUnmount: () => {
			const appWillUnmountHandlers = container.getAll<IAppWillUnmountHandler>(LifeCycle.AppWillUnmountHandler)
			appWillUnmountHandlers.forEach((appWillUnmountHandler) => appWillUnmountHandler.appWillUnmount())
		},
		getModule,
		getNamedModule,
		initCustomElements: () => initCustomElementsDS(viewerModel, wixCustomElements),
		...(process.env.NODE_ENV === 'test' ? { dsCarmi } : {}),
	}
}
